import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { QuestRoute } from '../../api/QuestRoutes';
import { formatCurrency, exportToPDF, generateId, shortHandDateTime, translate } from '../../utility/functions';
import Page from '../../layout/Page';
import Table from '../../table/Table';
import { useParams } from 'react-router-dom';
import Button from '../../forms/Button';
import HamQuest from '../../api/HamQuestGiver';
import Card from '../../forms/Card';
import Col from '../../layout/Col';
import Title from '../../forms/Title';
import Loading from '../../Loading';
import Row from '../../layout/Row';
import ModalFactory from '../../Modal';
import { SCREEN_MAX_WIDTH, SCREEN_WIDTH } from '../../../constants/config';

const HeaderRow = styled(Row)`
    display: flex;
    gap: 4em;
    justify-content: space-between;
    align-items: flex-start;
`
const ReportSegment = styled.div`
    border-bottom: 1px solid ${({ theme }) => theme.gray}40;
    margin-bottom: 0.5em;
    padding-bottom: 0.2em;
`
const CardWrap = styled(Card)`
    margin: 0.5em;
    min-width: 50em;

    @media (max-width: ${SCREEN_MAX_WIDTH}) {
        min-width: unset;
        width: 70svw;
    }
`
const Transaction = ({ ...props }) => {

    const { transactionId } = useParams();
    const cardRef = useRef();
    const [data, setData] = useState(null)

    useState(() => {
        HamQuest.get(QuestRoute.reports.transaction + transactionId)
            .then(res => {
                if (!res.transId) return;
                const itemTotal = res.items.reduce((a, b) => a + b.itemPrice * b.quantity, 0);
                const discountTotal = res.discounts.reduce((a, b) => a + b.amount, 0);

                const subTotal = itemTotal - discountTotal;
                const taxTotal = res.taxes.reduce((a, b) => a + b.amount, 0);
                const total = subTotal + taxTotal;

                let paymentTotal = 0;
                let changeTotal = 0;

                res.payments.forEach(e => {
                    if (e.paymentType === 'CreditCard') {
                        paymentTotal += e.cardPaymentResults?.reduce((a, b) => a + (b.authNumber ? b.amount : 0), 0);
                        changeTotal = paymentTotal - (itemTotal + taxTotal) || 0;
                    }
                    if (e.paymentType === 'Cash') {
                        paymentTotal += e.amount
                        // changeTotal = e.reduce((a, b) => a + b.amount, 0) - (total) || 0
                        changeTotal = e.amount - (total) || 0
                    }
                })

                setData({
                    ...res,
                    itemTotal,
                    discountTotal,
                    subTotal,
                    taxTotal,
                    total,
                    paymentTotal,
                    changeTotal,
                })
            })
            .catch(ModalFactory.catch)
    }, [])

    return (
        <Page title={`Transaction Report (${transactionId})`}>
            <Button
                disabled={!data}
                onClick={() => exportToPDF(cardRef, `Transaction Record (${transactionId})`)}
            >Export</Button>
            {data ?
                <CardWrap title='Transaction Record' icon='clipboard'>
                    <div ref={cardRef}>
                        <ReportSegment>
                            <HeaderRow wrap>
                                <Col style={{ flex: 1 }}>
                                    <HeaderRow>
                                        <b style={{ whiteSpace: 'nowrap' }}>Kiosk Date & Time</b>
                                        <span style={{ whiteSpace: 'nowrap' }}>{shortHandDateTime(data.kioskDateTime)}</span>
                                    </HeaderRow>
                                    <HeaderRow>
                                        <b>Trans Record ID</b>
                                        <span>{data.recordId}</span>
                                    </HeaderRow>
                                    <HeaderRow>
                                        <b>Transaction Type</b>
                                        <span>{data.transactionType}</span>
                                    </HeaderRow>
                                    <HeaderRow>
                                        <b>Record Type</b>
                                        <span>{data.recordType}</span>
                                    </HeaderRow>
                                    <HeaderRow>
                                        <b>Kiosk Trans ID</b>
                                        <span>{data.transId}</span>
                                    </HeaderRow>
                                    <HeaderRow>
                                        <b>Sequence Number</b>
                                        <span>{data.sequenceNum}</span>
                                    </HeaderRow>
                                </Col>

                                <Col style={{ flex: 1 }}>
                                    <HeaderRow>
                                        <b>Model</b>
                                        <span>{data.model}</span>
                                    </HeaderRow>
                                    <HeaderRow>
                                        <b>Version</b>
                                        <span>{data.version}</span>
                                    </HeaderRow>
                                    <HeaderRow>
                                        <b>MAC Address</b>
                                        <span>{data.macAddress}</span>
                                    </HeaderRow>
                                    <HeaderRow>
                                        <b>Terminal Name</b>
                                        <span>{data.terminalName}</span>
                                    </HeaderRow>
                                    <HeaderRow>
                                        <b>Terminal ID</b>
                                        <span>{data.terminalId}</span>
                                    </HeaderRow>
                                    <HeaderRow>
                                        <b>Site ID</b>
                                        <span>{data.siteId}</span>
                                    </HeaderRow>
                                </Col>
                            </HeaderRow>
                        </ReportSegment>

                        <ReportSegment>
                            <HeaderRow>
                                <Title>Totals</Title>
                            </HeaderRow>
                            <Col style={SCREEN_WIDTH ? {} : { width: '50%' }}>
                                <ReportSegment>
                                    <Col style={{ flex: 1 }}>
                                        <HeaderRow>
                                            <b>Item Total(s)</b>
                                            <span>{formatCurrency(data.itemTotal)}</span>
                                        </HeaderRow>
                                        <HeaderRow>
                                            <b>Discount Total(s)</b>
                                            <span>{formatCurrency(data.discountTotal)}</span>
                                        </HeaderRow>
                                    </Col>
                                </ReportSegment>
                                <ReportSegment>
                                    <Col style={{ flex: 1 }}>
                                        <HeaderRow>
                                            <b>Subtotal</b>
                                            <span>{formatCurrency(data.subTotal)}</span>
                                        </HeaderRow>
                                        <HeaderRow>
                                            <b>Tax Total</b>
                                            <span>{formatCurrency(data.taxTotal)}</span>
                                        </HeaderRow>
                                    </Col>
                                </ReportSegment>
                                <Col style={{ flex: 1 }}>
                                    <HeaderRow>
                                        <b>Total</b>
                                        <span>{formatCurrency(data.total)}</span>
                                    </HeaderRow>
                                    <HeaderRow>
                                        <b>Payment Total</b>
                                        <span>{formatCurrency(data.paymentTotal)}</span>
                                    </HeaderRow>
                                    <HeaderRow>
                                        <b>Change Total</b>
                                        <span>{formatCurrency(data.changeTotal)}</span>
                                    </HeaderRow>
                                </Col>
                            </Col>
                        </ReportSegment>

                        <ReportSegment>
                            <HeaderRow>
                                <Title>Items</Title>
                            </HeaderRow>
                            <Table
                                config={{
                                    disableTableControls: true,
                                    disableHeaderSearch: true,
                                    data: data.items,
                                    colSpacing: [6, 10, 10, 10],
                                    headers: [
                                        {
                                            label: 'Qty',
                                            propKey: 'quantity',
                                            type: 'number'
                                        }, {
                                            label: 'Item ID',
                                            propKey: 'itemId',
                                        }, {
                                            label: 'SKU',
                                            propKey: 'SKU',
                                        }, {
                                            label: 'Vended',
                                            propKey: 'vended',
                                        }, {
                                            label: 'Description',
                                            propKey: 'description',
                                        }, {
                                            label: 'Item Type',
                                            propKey: 'itemType',
                                        }, {
                                            label: 'Item Price',
                                            propKey: 'itemPrice',
                                            type: 'number'
                                        },
                                    ],
                                    renderFormat: (e) => (
                                        <tr key={generateId()}>
                                            <td>{e.quantity}</td>
                                            <td>{e.itemId}</td>
                                            <td>{e.SKU || '-'}</td>
                                            <td>{e.vended || '-'}</td>
                                            <td>{e.description}</td>
                                            <td>{translate(e.itemType)}</td>
                                            <td>{formatCurrency(e.itemPrice)}</td>
                                        </tr>
                                    )
                                }}
                            />
                        </ReportSegment>

                        <ReportSegment>
                            <HeaderRow>
                                <Title>Payments</Title>
                            </HeaderRow>
                            <Table
                                config={{
                                    disableTableControls: true,
                                    disableHeaderSearch: true,
                                    data: data.payments,
                                    colSpacing: [40],
                                    headers: [
                                        {
                                            label: 'Payment Type',
                                            propKey: 'paymentType',
                                        }, {
                                            label: 'Amount',
                                            propKey: 'amount',
                                        }, {
                                            label: 'Account Number',
                                            propKey: 'accountNumber',
                                        },
                                    ],
                                    renderFormat: (e) => (
                                        <tr key={generateId()}>
                                            <td>
                                                <Col>
                                                    {e.paymentType}
                                                    {
                                                        e.paymentType === 'CreditCard' &&
                                                        e.cardPaymentResults.map(f => (
                                                            <Col key={generateId} style={{ marginLeft: '1em' }}>
                                                                <HeaderRow><b>Card Type</b><span>{f.cardType}</span></HeaderRow>
                                                                <HeaderRow><b>Auth #</b><span>{f.authNumber}</span></HeaderRow>
                                                                <HeaderRow><b>Card #</b><span>{f.maskedCardNumber}</span></HeaderRow>
                                                            </Col>
                                                        ))

                                                    }
                                                </Col>
                                            </td>
                                            <td>{formatCurrency(e.amount)}</td>
                                            <td>{e.accountNumber || '-'}</td>
                                        </tr>
                                    )
                                }}
                            />
                        </ReportSegment>
                    </div>
                </CardWrap>
                : <Loading page key='Loading' />
            }
        </Page>
    );
}

export default Transaction;